import React, {FC, useState} from 'react';

import AboutModal from '@/components/AboutModal';
import LogoSixense from '@/components/logos/LogoSixense';
import RequestImagesPanel from '@/components/panels/RequestImagesPanel';
import SlidePanel from '@/components/SlidePanel';
import Taskbar, {Mode} from '@/components/Taskbar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

import {CARDS_WIDTH} from '@/config';
import {DateRange} from '@/domain/useCases/createImageSearchRequest';
import {SxProps} from '@mui/material';
import styled from '@mui/system/styled';
import UpdateProviderCredentialsPanel from '@/components/panels/UpdateProviderCredentialsPanel';
import {ProviderCredentials} from '@/domain/entities/ProviderCredentials';

const Main = styled(Box)({
  flexGrow: 1,
  padding: 0,
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0
});

export type LayoutProps = {
  mainContent: React.ReactElement,
  mode?: Mode,
  dateRange: Partial<DateRange>,
  isRequestImagesEnabled: boolean,
  hasDrawnGeometry: boolean,
  onDrawStart: () => void
  isLoadingSettings: boolean,
  providerCredentials?: Partial<ProviderCredentials>,
  onModeChanged: (mode?: Mode) => void
  onRequestImages: () => void,
  onAirbusSave: (airbusApiKey: string) => void,
  onCleosSave: (credentials: {cleosUsername: string, cleosPassword: string}) => void,
  onProvidersCredentialsChange: (field: keyof ProviderCredentials, value: string) => void,
  onDateRangeChange: (dateRange: Partial<DateRange>) => void,
  onLogout: () => void,
};

const Layout: FC<LayoutProps> = ({
  mainContent,
  mode,
  dateRange,
  isRequestImagesEnabled,
  hasDrawnGeometry,
  onDrawStart,
  isLoadingSettings,
  providerCredentials,
  onDateRangeChange,
  onModeChanged,
  onRequestImages,
  onAirbusSave,
  onCleosSave,
  onProvidersCredentialsChange,
  onLogout,
}) => {
  const [isAboutOpen, setAboutOpen] = useState(false);

  const handleAboutClicked = () => setAboutOpen(!isAboutOpen);

  return <>
    <Paper sx={styles.paperSx} elevation={0} id="Logo Sixense">
      <LogoSixense fill="#fff"/>
    </Paper>
    <Main>
      {mainContent}
    </Main>
    <Taskbar
      mode={mode}
      onModeChanged={onModeChanged}
      onAboutClicked={handleAboutClicked}
      onLogout={onLogout}
    />
    {
      mode === Mode.SEARCH_IMAGE &&
        <SlidePanel width={400}>
          <RequestImagesPanel
            dateRange={dateRange}
            isRequestEnabled={isRequestImagesEnabled}
            hasDrawnGeometry={hasDrawnGeometry}
            onDateRangeChange={onDateRangeChange}
            onDrawStart={onDrawStart}
            onRequest={onRequestImages}
          />
        </SlidePanel>
    }
    {
      mode === Mode.SETTINGS &&
        <SlidePanel width={400}>
          <UpdateProviderCredentialsPanel
            isLoading={isLoadingSettings}
            providerCredentials={providerCredentials}
            onAirbusSave={onAirbusSave}
            onCleosSave={onCleosSave}
            onProvidersCredentialsChange={onProvidersCredentialsChange}
          />
        </SlidePanel>
    }
    <AboutModal isOpen={isAboutOpen} onClose={handleAboutClicked}/>
  </>;
};

export default Layout;

const styles: Record<string, SxProps> = {
  paperSx: {
    position: 'fixed',
    bgcolor: 'transparent',
    width: CARDS_WIDTH,
    zIndex: 12500,
    p: 1
  },
  alert: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%)'
  }
};
